import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import ExportInfo, { PREVIEW_LIMIT } from "./Export-info";
import ExportDone from "./Export-done";
import SubscriptionPausedBanner from "../../common/SubscriptionPausedBanner";
import PaymentFailedBanner from "../../common/PaymentFailedBanner";
import {
  clearPendingSubmission,
  createPropertyExport,
  fetchExportCreditPreview,
  fetchExportOptions,
  fetchExportPreview,
  fetchPropertyExport,
  fetchPropertyExportDownload,
  idempotencyKeyFor,
} from "../../../helpers/propertyExports";
import { updateMEmberCredits } from "../../../states/actions/fetchSiteSettings";

/**
 * What actually happened, in the user's terms.
 *
 * The internal outcome names never appear: they are grouped into the only two
 * things a customer cares about - did this property get a contact, and did any
 * property not come through at all.
 */
export function exportSummary(status) {
  const total = status?.total || 0;
  const outcomes = status?.contact_outcomes || {};
  const count = (...names) => names.reduce((sum, name) => sum + (outcomes[name] || 0), 0);

  const withContacts = count("purchased", "cached");
  // Only properties we actually looked up. "not_attempted" means credit ran
  // out before we asked, which gets its own sentence below - counting it here
  // too reported the same properties twice, and "had none" is not even true
  // of them.
  const without = count("no_match", "failed");
  const notAttempted = outcomes.not_attempted || 0;
  const missing = status?.missing || 0;

  const parts = ["Your export is ready."];
  if (missing > 0) {
    parts.push(`${missing} of ${total} ${missing === 1 ? "property was" : "properties were"} unavailable.`);
  }
  // Always reported when contacts were asked for, not only when something went
  // wrong. An export that quietly returns blank contact columns for a handful
  // of properties still needs to say so, or the file looks like it failed.
  if (withContacts + without > 0) {
    parts.push(
      without > 0
        ? `${withContacts} of ${withContacts + without} got contacts; ${without} had none on file.`
        : `All ${withContacts} got contacts.`
    );
  }
  if (notAttempted > 0) {
    parts.push(`${notAttempted} were skipped because credits ran out.`);
  }

  return parts.join(" ");
}

const ACTIVE_EXPORT_KEY = "activePropertyExportId";
const PENDING_IDS_KEY = "pendingPropertyExportIds";
const POLL_INTERVAL_MS = 4000;
// About a minute of consecutive failures before we stop and say so.
const MAX_POLL_FAILURES = 8;
const TERMINAL_FAILURES = new Set(["failed", "expired"]);

function storedArray(storage, key) {
  try {
    const value = JSON.parse(storage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function startDownload(url) {
  const link = document.createElement("a");
  link.href = url;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** "1 credit", "2,500 credits"; a missing count reads as 0. */
const creditsCount = (value) => {
  const count = Math.max(0, Number(value) || 0);
  return `${count.toLocaleString()} ${count === 1 ? "credit" : "credits"}`;
};

const Export = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isPaused = useSelector(
    (state) => Number(state?.fetchSiteSettings?.site_settings?.member?.mem_pause_status) === 1
  );
  const totalAvailableCredits = useSelector(
    (state) => state.fetchSiteSettings.total_available_credits
  );
  const [parcelIds] = useState(() => {
    if (location.state?.parcel_ids?.length > 0) return location.state.parcel_ids;

    const saved = storedArray(sessionStorage, PENDING_IDS_KEY);
    if (saved.length > 0) return saved;

    const query = new URLSearchParams(location.search).get("parcels");
    return query ? query.split(",") : [];
  });
  const [options, setOptions] = useState({ columns: [], column_groups: [], default_columns: [] });
  const [activeExportId, setActiveExportId] = useState(
    () => localStorage.getItem(ACTIVE_EXPORT_KEY) || null
  );
  const [progress, setProgress] = useState(null);
  // The finished export stays on screen until the user leaves. Clearing it
  // would drop the page back to the selection form, which has nothing to show
  // once the selection is gone.
  const [finished, setFinished] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creditShortfall, setCreditShortfall] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewProperties, setPreviewProperties] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(parcelIds.length > 0);
  const [previewError, setPreviewError] = useState(false);
  const [previewRetry, setPreviewRetry] = useState(0);
  const submittingRef = useRef(false);
  const creditDialogRef = useRef(null);
  const creditCloseRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (parcelIds.length === 0) return undefined;

    let cancelled = false;
    setIsPreviewLoading(true);
    setPreviewError(false);

    fetchExportPreview(parcelIds, previewPage, PREVIEW_LIMIT)
      .then((preview) => {
        if (!cancelled) {
          // The server is the enforcement point and it has already dropped the
          // commercial rows a restricted member may not see: PropertyExport-
          // Controller::preview() skips them before building the response, and
          // the export itself refuses an all-commercial selection.
          //
          // There was a client-side filter here as well. It could never do
          // anything: a preview row is {id, parcel_id, address} only, with no
          // general_propertyTypeDetail/Category to test, so it dropped nothing,
          // the excluded count was always 0 and the warning never fired. It ran
          // per page off previewPage/previewRetry, so had it fired it would
          // have reported one page and repeated on every page change.
          //
          // Telling the member how many were excluded needs a real number from
          // the server — the page count cannot distinguish "commercial,
          // excluded" from "parcel not found in source". Left out rather than
          // guessed; see the note on this in the PR.
          setPreviewProperties(preview.properties || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewProperties([]);
          setPreviewError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [parcelIds, previewPage, previewRetry]);

  useEffect(() => {
    fetchExportOptions()
      .then(setOptions)
      .catch(() => toast.error("Export options could not be loaded. Please try again."));
  }, []);

  useEffect(() => {
    if (!activeExportId) return undefined;

    let cancelled = false;
    let timer;
    let failures = 0;

    // Every way an export can end runs this. Leaving any of it behind made the
    // next visit resume an export that was already over.
    //
    // The submission key is deliberately not touched: it is cleared the moment
    // the export is created, so nothing of this one's is left, and wiping the
    // store here would take another tab's in-flight key with it.
    const clearExportState = () => {
      localStorage.removeItem(ACTIVE_EXPORT_KEY);
      sessionStorage.removeItem(PENDING_IDS_KEY);
      setActiveExportId(null);
      setIsSubmitting(false);
      submittingRef.current = false;
    };

    const poll = async () => {
      try {
        const next = await fetchPropertyExport(activeExportId);
        if (cancelled) return;
        failures = 0;
        setProgress(next);

        if (["completed", "completed_with_warnings"].includes(next.status)) {
          const download = await fetchPropertyExportDownload(activeExportId);
          if (cancelled) return;
          clearExportState();
          dispatch(updateMEmberCredits());

          if (!download?.url) {
            setFinished({ status: next, url: null });
            toast.error(
              "Your export is ready, but its download link could not be created. Check Saved Exports."
            );
            return;
          }
          startDownload(download.url);
          setFinished({ status: next, url: download.url });
          const settled = next.status === "completed";
          const message = exportSummary(next);
          // Anything beyond the bare "ready" line is worth reading, so it waits
          // to be dismissed even when nothing actually went wrong.
          const hasDetail = message !== "Your export is ready.";
          toast[settled ? "success" : "warning"](message, {
            // A warning names something the user may need to act on, so it waits
            // to be dismissed rather than disappearing after a few seconds.
            autoClose: settled && ! hasDetail ? 5000 : false,
            closeOnClick: false,
          });
          return;
        }

        if (TERMINAL_FAILURES.has(next.status)) {
          clearExportState();
          toast.error(next.error || "The export could not be completed.");
          return;
        }

        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (error) {
        if (cancelled) return;

        // Bounded. An unbounded retry meant a status endpoint that kept failing
        // left the page on "Preparing export" forever, with nothing to act on.
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) {
          clearExportState();
          toast.error(
            "We lost contact with your export. It may still finish - check Saved Exports."
          );
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS * (failures + 1));
      }
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeExportId, dispatch]);

  const markPropertiesAsExported = () => {
    const existing = storedArray(localStorage, "exportedPropertyIds");
    localStorage.setItem(
      "exportedPropertyIds",
      JSON.stringify(Array.from(new Set([...existing, ...parcelIds])))
    );
    window.dispatchEvent(new Event("exportMarkersUpdated"));
  };

  const createExport = async (payload) => {
    const created = await createPropertyExport(payload, idempotencyKeyFor(payload));
    clearPendingSubmission(payload);
    localStorage.setItem(ACTIVE_EXPORT_KEY, created.id);
    setProgress(created);
    setActiveExportId(created.id);
    markPropertiesAsExported();
    toast.success("Your export is processing. You can leave this page and return later.");
  };

  const submissionFailed = (error) => {
    submittingRef.current = false;
    setIsSubmitting(false);
    const message = error?.response?.data?.message
      || Object.values(error?.response?.data?.errors || {})?.[0]?.[0]
      || "The export could not be started.";
    toast.error(message);
  };

  const previewFailed = (error, payload) => {
    const status = error?.response?.status;
    const unavailable = !status || [404, 405, 429].includes(status) || status >= 500;
    if (!unavailable) return false;

    previousFocusRef.current = document.activeElement;
    setCreditShortfall({ unavailable: true, payload });
    submittingRef.current = false;
    setIsSubmitting(false);
    return true;
  };

  const checkExportCredits = async (payload) => {
    let preview;
    try {
      preview = await fetchExportCreditPreview(payload.parcel_ids, payload.columns);
    } catch (error) {
      if (!previewFailed(error, payload)) submissionFailed(error);
      return false;
    }

    const shortfall = preview?.shortfall;
    if (typeof shortfall !== "number" || !Number.isFinite(shortfall) || shortfall < 0) {
      previousFocusRef.current = document.activeElement;
      setCreditShortfall({ unavailable: true, payload });
      submittingRef.current = false;
      setIsSubmitting(false);
      return false;
    }

    if (shortfall > 0) {
      previousFocusRef.current = document.activeElement;
      setCreditShortfall({ ...preview, payload });
      submittingRef.current = false;
      setIsSubmitting(false);
      return false;
    }

    return true;
  };

  const createAfterCreditCheck = async (payload) => {
    if (!await checkExportCredits(payload)) return;

    try {
      await createExport(payload);
    } catch (error) {
      submissionFailed(error);
    }
  };

  const handleExport = async (form) => {
    if (submittingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    const payload = { ...form, parcel_ids: parcelIds };
    await createAfterCreditCheck(payload);
  };

  const retryCreditPreview = async () => {
    if (!creditShortfall || submittingRef.current) return;

    const { payload } = creditShortfall;
    setCreditShortfall(null);
    submittingRef.current = true;
    setIsSubmitting(true);
    await createAfterCreditCheck(payload);
  };

  const continueExport = async () => {
    if (!creditShortfall || submittingRef.current) return;

    const { payload } = creditShortfall;
    setCreditShortfall(null);
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await createExport(payload);
    } catch (error) {
      submissionFailed(error);
    }
  };

  useEffect(() => {
    if (!creditShortfall) return undefined;

    creditCloseRef.current?.focus();
    const previousFocus = previousFocusRef.current;
    const handleDialogKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setCreditShortfall(null);
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(creditDialogRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || []);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleDialogKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDialogKeyDown);
      previousFocus?.focus();
    };
  }, [creditShortfall]);

  if (isPaused) {
    return <><ToastContainer /><SubscriptionPausedBanner /></>;
  }

  return (
    <>
      {creditShortfall && (
        <div
          ref={creditDialogRef}
          className="popup active lg export_credit_popup"
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-credit-title"
        >
          <div className="table_dv">
            <div className="table_cell">
              <div className="_inner">
                <button
                  ref={creditCloseRef}
                  type="button"
                  className="x_btn"
                  aria-label="Close"
                  onClick={() => setCreditShortfall(null)}
                />
                <div className="popup_inner">
                  <h4 id="export-credit-title">
                    {creditShortfall.unavailable
                      ? "Credit check unavailable"
                      : `You're missing ${creditsCount(creditShortfall.shortfall)}`}
                  </h4>
                  {creditShortfall.unavailable ? (
                    <p>
                      We could not verify which contacts are already yours. Retry the
                      check, or continue and the export will use only available credits.
                    </p>
                  ) : (
                    <p className="export_credit_intro">
                      {/* Never promise "all" contacts: a large export can exceed even
                          the top plan's limit. */}
                      {`Upgrade your plan to increase your credit limits, or continue with the remaining ${creditsCount(creditShortfall.credits_available)}.`}
                    </p>
                  )}
                  <div className="export_credit_actions">
                    {creditShortfall.unavailable ? (
                      <button type="button" className="site_btn" onClick={retryCreditPreview}>
                        Retry Credit Check
                      </button>
                    ) : (
                      <Link className="site_btn" to="/pricing">Upgrade Plan</Link>
                    )}
                    <button type="button" className="site_btn blank" onClick={continueExport}>
                      Continue Export
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        className="export_page_content"
        aria-hidden={creditShortfall ? "true" : undefined}
        inert={creditShortfall ? "" : undefined}
      >
        <ToastContainer />
        <SubscriptionPausedBanner />
        <PaymentFailedBanner />
        {activeExportId ? (
          <section className="export_sec">
            <div className="contain-fluid">
              <h3>Preparing export</h3>
            {/* Deliberately only progress and spend. The per-property contact
                outcomes behind this are internal vocabulary - "not requested",
                "cached" - and mean nothing to the person waiting. */}
            {(() => {
              const total = progress?.total || parcelIds.length || 0;
              const done = progress?.processed || 0;
              const percent = total > 0 ? Math.round((done / total) * 100) : 0;

              return (
                <>
                  <div
                    className="export_progress"
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Export progress"
                  >
                    <span style={{ width: `${percent}%` }} />
                  </div>
                  <p className="export_progress_line">
                    {done.toLocaleString()} of {total.toLocaleString()} properties
                    {" "}({percent}%)
                  </p>
                  {progress?.credits_spent > 0 && (
                    <p className="export_progress_line">
                      {progress.credits_spent.toLocaleString()}
                      {progress.credits_spent === 1 ? " credit" : " credits"} used so far
                    </p>
                  )}
                </>
              );
            })()}
            <p>You can leave this page. Progress will resume here when you return.</p>
            </div>
          </section>
        ) : finished ? (
          <ExportDone summary={exportSummary(finished.status)} url={finished.url} />
        ) : (
          <ExportInfo
            properties={previewProperties}
            propertyCount={parcelIds.length}
            previewPage={previewPage}
            previewPages={Math.max(1, Math.ceil(parcelIds.length / PREVIEW_LIMIT))}
            onPreviewPageChange={setPreviewPage}
            onPreviewRetry={() => setPreviewRetry((attempt) => attempt + 1)}
            isPreviewLoading={isPreviewLoading}
            previewError={previewError}
            availableColumns={options.columns}
            columnGroups={options.column_groups || []}
            defaultColumns={options.default_columns || []}
            phoneFilters={options.phone_filters || null}
            handleExportPdf={handleExport}
            isFormProcessing={isSubmitting}
            total_available_credits={totalAvailableCredits}
          />
        )}
      </div>
    </>
  );
};


export default Export;
